import type { Action, ApiError, MetaAction, PayloadAction, Page } from 'modules/global/generic.types';
import { onSuccessRequest as onHasMoreSuccessRequest } from 'modules/global/reducers/generic.hasMore';
import { beforeRequest as idsBeforeRequest, setAll as idsSetAll } from 'modules/global/reducers/generic.ids';
import { initialState as initialStatePage } from 'modules/global/reducers/generic.page';
import type { MatcherFunction, WrapperReducerConfig } from 'modules/global/reducers/reducers.types';
import type { SimpleSearchState } from 'modules/global/reducers/simpleSearchReducer';
import { isStartRequest, isSuccessRequest, isFailureRequest } from 'modules/global/reducers/utils';
import type { MayBe } from 'modules/global/typing/common/common';
import { createReducer } from 'utils/ReduxUtils';

/** One select's own paginated slice: same five fields as a simple search. */
export type GenericSelectSlice = SimpleSearchState;

/** Every select of a page, keyed by the `meta.selectId` its actions carry. */
export type GenericSelectsSlice = Record<string, GenericSelectSlice>;

const emptySlice: GenericSelectSlice = {
  loading: false,
  ids: [],
  error: null,
  hasMore: false,
  page: {
    ...initialStatePage,
  },
};

const getId = ({ meta }: { meta?: MetaAction }): MayBe<string> =>
  (typeof meta?.selectId === 'string' ? meta.selectId : undefined);

/** Default matcher: an action only reaches a select slice when it names the select it targets. */
const hasSelectId: MatcherFunction = action => Boolean(getId(action));

/**
 * Resets the slice of the select an action targets. Unlike the request matchers below, this one
 * ignores actions that carry no `meta.selectId`.
 */
export const createClearSelectReducer =
  () =>
    (state: GenericSelectsSlice, action: Action): GenericSelectsSlice => {
      const selectId = getId(action);
      if (!selectId) {
        return state;
      }
      return {
        ...state,
        [selectId]: { ...emptySlice },
      };
    };

/** A page of entities, as `generic.ids` needs to read it. */
type SelectSuccessPayload<T> = { content: Array<T>; page: Page };

const genericSelectSlice = <T = { id: string }>(
  actionRoot: string,
  { global, reducers = {}, matchers = [] }: WrapperReducerConfig<GenericSelectsSlice, T> = {}
) => {
  const initialize = (state: GenericSelectsSlice, selectId: string): GenericSelectsSlice => ({
    ...state,
    [selectId]: { ...(state[selectId] ?? emptySlice) },
  });

  // An action without `meta.selectId` only reaches the reducers when the consumer replaced the
  // default matcher with its own; the slice is then stored under the literal key "undefined".
  const sliceKey = (action: Action): string => getId(action) ?? String(undefined);

  const onRequestMatcher = (baseState: GenericSelectsSlice, action: PayloadAction<{ request: { params?: { page?: unknown } } }>) => {
    const selectId = sliceKey(action);
    const state = initialize(baseState, selectId);

    return {
      ...state,
      [selectId]: {
        ...state[selectId],
        error: null,
        loading: true,
        ids: idsBeforeRequest<T>(global)(state[selectId].ids, action),
      },
    };
  };

  const onSuccessRequest = (baseState: GenericSelectsSlice, action: PayloadAction<SelectSuccessPayload<T>>) => {
    const selectId = sliceKey(action);
    const state = initialize(baseState, selectId);

    return {
      ...state,
      [selectId]: {
        ...state[selectId],
        loading: false,
        page: action.payload.page,
        ids: idsSetAll<T>(global)(state[selectId].ids, action),
        hasMore: onHasMoreSuccessRequest(state[selectId].hasMore, action),
      },
    };
  };

  const onFailureRequest = (baseState: GenericSelectsSlice, action: PayloadAction<ApiError>) => {
    const selectId = sliceKey(action);
    const state = initialize(baseState, selectId);

    return {
      ...state,
      [selectId]: {
        ...emptySlice,
        error: action.payload,
      },
    };
  };

  // `global.matcher` replaces the default `hasSelectId`; the rest of `global` (`selectId` /
  // `filter` / `paginationSlice`) is forwarded to `generic.ids` above.
  const matcherConfig = { matcher: global?.matcher ?? hasSelectId };

  return createReducer<GenericSelectsSlice>({}, reducers, [
    {
      matcher: isStartRequest(actionRoot, matcherConfig),
      reducer: onRequestMatcher,
    },
    {
      matcher: isSuccessRequest(actionRoot, matcherConfig),
      reducer: onSuccessRequest,
    },
    {
      matcher: isFailureRequest(actionRoot, matcherConfig),
      reducer: onFailureRequest,
    },
    ...matchers,
  ]);
};

export default genericSelectSlice;
